import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { GiGuitar } from 'react-icons/gi';
import { FiLogOut, FiShoppingCart, FiHome, FiShoppingBag, FiUser, FiUserPlus } from 'react-icons/fi';
import { Sizes } from '../utils/sizes';
import { Colors } from '../utils/colors';
import { logout, selectCurrentUser, selectIsAuthenticated } from '../redux/slices/authSlice';
import { clearOnLogout } from '../redux/slices/ebookAccessSlice';

/* ─── Animações ─────────────────────────────────────────────────────────── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Layout principal ───────────────────────────────────────────────────── */
const HeaderContainer = styled.header`
  width: 100%;
  background: rgba(8, 8, 8, 0.92);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255, 215, 0, 0.15);
  box-shadow: 0 1px 0 rgba(255,215,0,0.06), 0 8px 40px rgba(0,0,0,0.7);
  padding: 0 24px;
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    padding: 0 14px;
  }
`;

const HeaderContent = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  height: 62px;
  display: flex;
  align-items: center;
  gap: 0;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    height: 54px;
  }
`;

/* ─── Logo ───────────────────────────────────────────────────────────────── */
const LogoBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 36px;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:hover { opacity: 0.82; }
  &:focus-visible { outline: 2px solid ${Colors.yellow}; outline-offset: 4px; border-radius: 4px; }

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    margin-right: 0;
  }
`;

const LogoBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: ${Colors.yellow};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  flex-shrink: 0;
`;

const LogoText = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.2px;
  white-space: nowrap;

  & strong { color: ${Colors.yellow}; font-weight: 800; }

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    display: none;
  }
`;

/* ─── Nav ────────────────────────────────────────────────────────────────── */
const Nav = styled.nav`
  display: flex;
  align-items: stretch;
  gap: 0;
  flex: 1;
  height: 100%;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    display: none;
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  position: relative;
  color: rgba(255,255,255,0.45);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 14px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  transition: color 0.15s;
  white-space: nowrap;
  height: 100%;
  display: flex;
  align-items: center;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 14px;
    right: 14px;
    height: 2px;
    background: ${Colors.yellow};
    transform: scaleX(0);
    transition: transform 0.18s ease;
    border-radius: 2px 2px 0 0;
  }

  &:hover {
    color: rgba(255,255,255,0.9);
    &::after { transform: scaleX(0.5); }
  }

  &.active {
    color: ${Colors.yellow};
    &::after { transform: scaleX(1); }
  }

  &:focus-visible {
    outline: 2px solid ${Colors.yellow};
    outline-offset: -4px;
    border-radius: 4px;
  }
`;

/* ─── Lado direito ───────────────────────────────────────────────────────── */
const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

/* ─── Dropdown de usuário ────────────────────────────────────────────────── */
const UserMenuWrapper = styled.div`
  position: relative;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    display: none;
  }
`;

const UserTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 5px 10px 5px 5px;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s;

  &:hover, &[aria-expanded="true"] {
    border-color: rgba(255,215,0,0.4);
    background: rgba(255,215,0,0.06);
  }
  &:focus-visible { outline: 2px solid ${Colors.yellow}; outline-offset: 2px; border-radius: 8px; }
`;

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: linear-gradient(135deg, ${Colors.yellow} 0%, #e6a800 100%);
  color: #111;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.3px;
  flex-shrink: 0;
`;

const TriggerName = styled.span`
  color: rgba(255,255,255,0.72);
  font-size: 12.5px;
  font-weight: 600;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChevronIcon = styled.span`
  color: rgba(255,255,255,0.3);
  font-size: 10px;
  margin-left: 2px;
  transition: transform 0.18s;
  display: inline-block;
  ${(p) => p.open && 'transform: rotate(180deg);'}
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 220px;
  background: #111;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,215,0,0.06);
  overflow: hidden;
  animation: ${fadeIn} 0.16s ease;
  z-index: 200;
`;

const DropdownHeader = styled.div`
  padding: 14px 16px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.07);

  & .full-name {
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 2px;
  }

  & .email {
    color: rgba(255,255,255,0.35);
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  background: none;
  border: none;
  color: rgba(255,255,255,0.6);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, color 0.15s;

  & .icon { font-size: 15px; opacity: 0.7; flex-shrink: 0; }

  &:hover {
    background: rgba(255,255,255,0.05);
    color: #fff;
    & .icon { opacity: 1; }
  }

  &.danger {
    color: rgba(248,113,113,0.7);
    &:hover { background: rgba(248,113,113,0.08); color: #f87171; }
  }

  &:focus-visible { outline: none; background: rgba(255,255,255,0.06); }
`;

const LogoutConfirm = styled.div`
  padding: 12px 16px 14px;
  border-top: 1px solid rgba(255,255,255,0.07);
  animation: ${fadeIn} 0.14s ease;
`;

const ConfirmLabel = styled.p`
  color: rgba(255,255,255,0.65);
  font-size: 12px;
  margin: 0 0 10px 0;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 6px;
`;

const ConfirmBtn = styled.button`
  flex: 1;
  padding: 7px 0;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;

  &:hover { opacity: 0.85; }

  &.yes {
    background: #ef4444;
    color: #fff;
  }

  &.no {
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.6);
  }
`;

/* ─── Carrinho ───────────────────────────────────────────────────────────── */
const CartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  background: ${Colors.yellow};
  border: none;
  color: #111;
  padding: 8px 16px;
  border-radius: 9px;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  white-space: nowrap;
  transition: background 0.18s, box-shadow 0.18s, transform 0.18s;
  box-shadow: 0 2px 10px rgba(255,215,0,0.2);

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    padding: 0;
    width: 38px;
    height: 38px;
    border-radius: 9px;
    justify-content: center;
    & .cart-label { display: none; }
  }

  &:hover {
    background: #ffe54e;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(255,215,0,0.35);
  }

  &:focus-visible { outline: 2px solid ${Colors.yellow}; outline-offset: 2px; }

  & .cart-icon { font-size: 16px; }

  & .cart-badge {
    position: absolute;
    top: -7px;
    right: -7px;
    background: #ef4444;
    color: #fff;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 900;
    border: 2px solid rgba(8,8,8,0.92);
  }
`;

/* ─── Hambúrguer ─────────────────────────────────────────────────────────── */
const MenuToggle = styled.button`
  display: none;
  width: 38px;
  height: 38px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 9px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;

  @media (max-width: ${Sizes.mediaQueryBreak2}) { display: flex; }

  &:hover { background: rgba(255,215,0,0.08); border-color: rgba(255,215,0,0.3); }
  &:focus-visible { outline: 2px solid ${Colors.yellow}; outline-offset: 2px; }

  .bars { position: relative; width: 18px; height: 12px; display: inline-block; }
  .bars span {
    position: absolute; left: 0; right: 0; height: 2px;
    background: rgba(255,255,255,0.6); display: block; border-radius: 2px;
    transition: transform 240ms cubic-bezier(.2,.9,.2,1), opacity 140ms ease;
  }
  .bars span.top    { top: 0; }
  .bars span.middle { top: 5px; }
  .bars span.bottom { bottom: 0; }

  &[aria-expanded="true"] .bars span { background: ${Colors.yellow}; }
  &[aria-expanded="true"] .bars span.top    { transform: translateY(5px) rotate(45deg); }
  &[aria-expanded="true"] .bars span.middle { opacity: 0; }
  &[aria-expanded="true"] .bars span.bottom { transform: translateY(-5px) rotate(-45deg); }
`;

/* ─── Mobile drawer ──────────────────────────────────────────────────────── */
const Overlay = styled.div`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(3px);
  z-index: 998;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    display: ${(p) => (p.open ? 'block' : 'none')};
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0; right: 0;
  width: min(290px, 86vw);
  height: 100vh;
  background: #0a0a0a;
  border-left: 1px solid rgba(255,255,255,0.08);
  z-index: 999;
  flex-direction: column;
  padding: 18px 12px 24px;
  overflow-y: auto;
  box-sizing: border-box;
  transform: translateX(${(p) => (p.open ? '0' : '100%')});
  transition: transform 230ms ease;
  box-shadow: -16px 0 48px rgba(0,0,0,0.8);

  @media (max-width: ${Sizes.mediaQueryBreak2}) { display: flex; }
`;

const DrawerTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const DrawerClose = styled.button`
  width: 32px; height: 32px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 8px;
  color: rgba(255,255,255,0.45);
  font-size: 14px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s;

  &:hover { background: rgba(255,215,0,0.1); color: ${Colors.yellow}; }
`;

const DrawerBrand = styled.div`
  display: flex; align-items: center; gap: 8px;
  & .badge { width: 28px; height: 28px; border-radius: 7px; background: ${Colors.yellow}; display: flex; align-items: center; justify-content: center; font-size: 15px; }
  & .name { font-size: 14px; font-weight: 700; color: #fff; }
  & .name strong { color: ${Colors.yellow}; }
`;

const DrawerUser = styled.div`
  display: flex; align-items: center; gap: 10px;
  background: rgba(255,215,0,0.05);
  border: 1px solid rgba(255,215,0,0.12);
  border-radius: 10px;
  padding: 11px 13px;
  margin-bottom: 16px;
`;

const DrawerAvatar = styled.div`
  width: 34px; height: 34px;
  border-radius: 8px;
  background: ${Colors.yellow};
  color: #111;
  font-size: 12px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;

const DrawerUserText = styled.div`
  min-width: 0;
  & .name { color: #fff; font-size: 13px; font-weight: 700; }
  & .email { color: rgba(255,255,255,0.35); font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const DrawerSection = styled.div`
  color: rgba(255,255,255,0.22);
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 1.2px;
  padding: 14px 4px 6px;
`;

const DrawerItem = styled.button`
  width: 100%;
  display: flex; align-items: center; gap: 11px;
  padding: 10px 12px;
  background: none; border: none;
  color: rgba(255,255,255,0.6);
  font-size: 13px; font-weight: 600;
  cursor: pointer; text-align: left; border-radius: 8px;
  transition: background 0.14s, color 0.14s;
  margin-bottom: 1px;

  & .icon { font-size: 15px; width: 20px; text-align: center; flex-shrink: 0; }

  &:hover { background: rgba(255,255,255,0.05); color: #fff; }

  &.highlight {
    background: rgba(255,215,0,0.08);
    color: ${Colors.yellow};
    border: 1px solid rgba(255,215,0,0.18);
  }

  &.danger { color: rgba(248,113,113,0.65); &:hover { background: rgba(248,113,113,0.07); color: #f87171; } }
`;

const DrawerLogoutConfirm = styled.div`
  background: rgba(239,68,68,0.07);
  border: 1px solid rgba(239,68,68,0.2);
  border-radius: 10px;
  padding: 12px 13px;
  margin-top: 2px;
  animation: ${fadeIn} 0.14s ease;

  & p { color: rgba(255,255,255,0.6); font-size: 12px; margin: 0 0 10px; }
`;

const DrawerConfirmRow = styled.div`
  display: flex; gap: 6px;

  & button {
    flex: 1; padding: 8px 0; border-radius: 7px;
    font-size: 12px; font-weight: 700; cursor: pointer; border: none;
    transition: opacity 0.15s;
    &:hover { opacity: 0.82; }
    &.yes { background: #ef4444; color: #fff; }
    &.no  { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); }
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════ */

const ShopHeader = ({ currentPage = 'home' }) => {
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const cartItems  = useSelector((state) => state.cart?.items || []);
  const totalItems = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser     = useSelector(selectCurrentUser);

  const [menuOpen,        setMenuOpen]        = useState(false);
  const [dropdownOpen,    setDropdownOpen]     = useState(false);
  const [confirmLogout,   setConfirmLogout]    = useState(false);
  const [mobileLogout,    setMobileLogout]     = useState(false);

  const dropdownRef = useRef(null);

  const firstName = currentUser?.name?.split(' ')[0] || 'você';
  const initials  = (currentUser?.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setConfirmLogout(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navigateTo = (path) => {
    navigate(path);
    setMenuOpen(false);
    setDropdownOpen(false);
    setConfirmLogout(false);
    setMobileLogout(false);
  };

  const doLogout = () => {
    dispatch(logout());
    dispatch(clearOnLogout());
    navigate('/');
    setMenuOpen(false);
    setDropdownOpen(false);
    setConfirmLogout(false);
    setMobileLogout(false);
  };

  return (
    <>
      <HeaderContainer>
        <HeaderContent>

          {/* Logo */}
          <LogoBtn onClick={() => navigateTo('/')} aria-label="Violão Católico – início">
            <LogoBadge><GiGuitar /></LogoBadge>
            <LogoText>Violão <strong>Católico</strong></LogoText>
          </LogoBtn>

          {/* Nav desktop */}
          <Nav role="navigation" aria-label="Menu principal">
            <NavLink className={currentPage === 'home'      ? 'active' : ''} onClick={() => navigateTo('/')}>Início</NavLink>
            <NavLink className={currentPage === 'shop'      ? 'active' : ''} onClick={() => navigateTo('/loja')}>Loja</NavLink>
            <NavLink className={currentPage === 'curso'     ? 'active' : ''} onClick={() => navigateTo('/curso-de-fingerstyle')}>Curso</NavLink>
            <NavLink className={currentPage === 'downloads' ? 'active' : ''} onClick={() => navigateTo('/minhas-compras')}>Minhas Compras</NavLink>
            {!isAuthenticated && (
              <>
                <NavLink className={currentPage === 'login'    ? 'active' : ''} onClick={() => navigateTo('/login')}>Entrar</NavLink>
                <NavLink className={currentPage === 'cadastro' ? 'active' : ''} onClick={() => navigateTo('/cadastro')}>Cadastrar</NavLink>
              </>
            )}
          </Nav>

          {/* Direita */}
          <RightGroup>

            {/* Usuário logado — dropdown */}
            {isAuthenticated && (
              <UserMenuWrapper ref={dropdownRef}>
                <UserTrigger
                  onClick={() => { setDropdownOpen((v) => !v); setConfirmLogout(false); }}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                >
                  <Avatar>{initials}</Avatar>
                  <TriggerName>{firstName}</TriggerName>
                  <ChevronIcon open={dropdownOpen}>▾</ChevronIcon>
                </UserTrigger>

                {dropdownOpen && (
                  <Dropdown role="menu">
                    <DropdownHeader>
                      <div className="full-name">{currentUser?.name || 'Usuário'}</div>
                      <div className="email">{currentUser?.email || ''}</div>
                    </DropdownHeader>

                    <DropdownItem onClick={() => navigateTo('/minhas-compras')}>
                      <span className="icon"><FiShoppingBag /></span> Minhas Compras
                    </DropdownItem>

                    {!confirmLogout ? (
                      <DropdownItem className="danger" onClick={() => setConfirmLogout(true)}>
                        <span className="icon"><FiLogOut /></span> Sair
                      </DropdownItem>
                    ) : (
                      <LogoutConfirm>
                        <ConfirmLabel>Tem certeza que deseja sair?</ConfirmLabel>
                        <ConfirmActions>
                          <ConfirmBtn className="yes" onClick={doLogout}>Sair</ConfirmBtn>
                          <ConfirmBtn className="no"  onClick={() => setConfirmLogout(false)}>Cancelar</ConfirmBtn>
                        </ConfirmActions>
                      </LogoutConfirm>
                    )}
                  </Dropdown>
                )}
              </UserMenuWrapper>
            )}

            {/* Carrinho */}
            <CartButton onClick={() => navigateTo('/checkout')} aria-label={`Carrinho – ${totalItems} item(s)`}>
              <span className="cart-icon" aria-hidden="true"><FiShoppingCart /></span>
              <span className="cart-label">Carrinho</span>
              {totalItems > 0 && <span className="cart-badge" aria-label={`${totalItems} itens`}>{totalItems}</span>}
            </CartButton>

            {/* Hambúrguer mobile */}
            <MenuToggle
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
            >
              <span className="bars" aria-hidden="true">
                <span className="top" />
                <span className="middle" />
                <span className="bottom" />
              </span>
            </MenuToggle>

          </RightGroup>
        </HeaderContent>
      </HeaderContainer>

      {/* Overlay mobile */}
      <Overlay open={menuOpen} onClick={() => { setMenuOpen(false); setMobileLogout(false); }} />

      {/* Drawer mobile */}
      <MobileMenu open={menuOpen} role="navigation" aria-label="Menu mobile">

        <DrawerTopRow>
          <DrawerBrand>
            <div className="badge"><GiGuitar /></div>
            <span className="name">Violão <strong>Católico</strong></span>
          </DrawerBrand>
          <DrawerClose onClick={() => { setMenuOpen(false); setMobileLogout(false); }} aria-label="Fechar menu">✕</DrawerClose>
        </DrawerTopRow>

        {isAuthenticated && (
          <DrawerUser>
            <DrawerAvatar>{initials}</DrawerAvatar>
            <DrawerUserText>
              <div className="name">{currentUser?.name || 'Usuário'}</div>
              <div className="email">{currentUser?.email || ''}</div>
            </DrawerUserText>
          </DrawerUser>
        )}

        <DrawerSection>Navegação</DrawerSection>
        <DrawerItem onClick={() => navigateTo('/')}><span className="icon"><FiHome /></span>Início</DrawerItem>
        <DrawerItem onClick={() => navigateTo('/loja')}><span className="icon"><FiShoppingBag /></span>Loja</DrawerItem>
        <DrawerItem className="highlight" onClick={() => navigateTo('/curso-de-fingerstyle')}><span className="icon"><GiGuitar /></span>Curso</DrawerItem>

        <DrawerSection>Minha Conta</DrawerSection>
        <DrawerItem onClick={() => navigateTo('/minhas-compras')}><span className="icon"><FiShoppingBag /></span>Minhas Compras</DrawerItem>
        <DrawerItem onClick={() => navigateTo('/checkout')}>
          <span className="icon"><FiShoppingCart /></span>Carrinho {totalItems > 0 && `(${totalItems})`}
        </DrawerItem>

        {isAuthenticated ? (
          <>
            <DrawerSection>Sessão</DrawerSection>
            {!mobileLogout ? (
              <DrawerItem className="danger" onClick={() => setMobileLogout(true)}>
                <span className="icon"><FiLogOut /></span>Sair da conta
              </DrawerItem>
            ) : (
              <DrawerLogoutConfirm>
                <p>Tem certeza que deseja sair?</p>
                <DrawerConfirmRow>
                  <button className="yes" onClick={doLogout}>Sair</button>
                  <button className="no"  onClick={() => setMobileLogout(false)}>Cancelar</button>
                </DrawerConfirmRow>
              </DrawerLogoutConfirm>
            )}
          </>
        ) : (
          <>
            <DrawerSection>Acesso</DrawerSection>
            <DrawerItem onClick={() => navigateTo('/login')}><span className="icon"><FiUser /></span>Entrar</DrawerItem>
            <DrawerItem onClick={() => navigateTo('/cadastro')}><span className="icon"><FiUserPlus /></span>Criar Conta</DrawerItem>
          </>
        )}

      </MobileMenu>
    </>
  );
};

export default ShopHeader;
